import type { AgenticLogicBudgetBasis } from "../../../dtos/agentic-logic/publish";
import {
  AGENTIC_LOGIC_SURFACE_LABEL,
  type AgenticLogicSurface,
  AgenticLogicSurfaceList,
} from "../../../dtos/agentic-logic/surface";

// ---------------------------------------------------------------------------
// Runtime-budget constants for rule evaluation. Declared ONCE here so the
// required-tests harness (tier 1) and the publish gate registry (tier 2)
// read the same numbers by construction rather than by coincidence.
//
// AGENTIC_LOGIC_RUNTIME_BUDGET_MAX_MS lives in dtos/agentic-logic/publish
// instead — it is a field cap on AgenticLogicPlacementAssignmentDto, not
// merely a gates concern.
// ---------------------------------------------------------------------------

/**
 * Wire default for `AgenticLogicTemplateRunnerInput.timeoutBudgetMs` when a
 * caller omits it — nothing else.
 *
 * It is deliberately NOT the rule-evaluation default. A rule's budget is
 * derived from the surfaces its placements render on
 * ({@link agenticLogicTightestSurfaceBudgetMs}), and every serving path passes
 * an explicit deadline, so this value is reached only by a caller that names
 * no budget at all.
 *
 * It must stay at or below the tightest surface cap. A default above the caps
 * can never be the effective serve-time budget, so it would only let the gate
 * promise something serving will not honour — which is exactly the drift that
 * put this constant at 5000 while its own test, the template-runner test and
 * the DTO description all still said 1000.
 */
export const AGENTIC_LOGIC_TEMPLATE_RUNNER_DEFAULT_BUDGET_MS = 1000;

/**
 * Base per-surface evaluation caps, in ms, with NO environment relaxation
 * applied. Ordered tight to loose: checkout blocks a shopper mid-purchase,
 * post-purchase renders after the order is banked.
 *
 * Base, not effective: the broker multiplies these by 10 under `IS_LOCAL` so a
 * tunnelled dev read does not trip a deadline. That multiplier stays in
 * node-backend on purpose. This package is a wire contract bundled into
 * clients and Lambdas, and an exported constant whose value depends on
 * `process.env` at module-eval time is non-deterministic per bundle and
 * effectively untestable.
 *
 * `satisfies` rather than a bare annotation so adding a surface to
 * {@link AgenticLogicSurfaceList} without a cap is a compile error here rather
 * than an `undefined` budget at serve time.
 */
export const AGENTIC_LOGIC_SURFACE_BASE_CAP_MS = {
  checkout: 3000,
  onsite: 4000,
  "post-purchase": 5000,
} as const satisfies Record<AgenticLogicSurface, number>;

/**
 * The evaluation budget a rule must fit, given the surfaces its enabled
 * placements render on: the tightest cap among them.
 *
 * An UNASSIGNED rule (empty `surfaces`) gets the tightest cap overall, not the
 * loosest. The publish job freezes a rule's budget at publish time and the
 * gate never re-runs on assignment, so gating an unassigned rule loosely lets
 * it be assigned to checkout afterwards and time out at serve time. The
 * tightest cap cannot be invalidated by any later assignment, which is what
 * makes the gate's promise true: this rule fits anywhere it can be placed.
 */
export function agenticLogicTightestSurfaceBudgetMs(
  surfaces: readonly AgenticLogicSurface[]
): number {
  if (surfaces.length === 0) {
    return Math.min(...Object.values(AGENTIC_LOGIC_SURFACE_BASE_CAP_MS));
  }
  return Math.min(
    ...surfaces.map((surface) => AGENTIC_LOGIC_SURFACE_BASE_CAP_MS[surface])
  );
}

/**
 * The surface whose cap decides the budget for a set: the argmin of
 * {@link AGENTIC_LOGIC_SURFACE_BASE_CAP_MS} over it, with an empty set reading
 * as "every surface it could be placed on" — exactly the reading
 * {@link agenticLogicTightestSurfaceBudgetMs} takes, so the surface a sentence
 * names and the number it quotes cannot disagree.
 */
function tightestSurface(
  surfaces: readonly AgenticLogicSurface[]
): AgenticLogicSurface {
  const candidates = surfaces.length > 0 ? surfaces : AgenticLogicSurfaceList;
  return candidates.reduce((tightest, surface) =>
    AGENTIC_LOGIC_SURFACE_BASE_CAP_MS[surface] <
    AGENTIC_LOGIC_SURFACE_BASE_CAP_MS[tightest]
      ? surface
      : tightest
  );
}

/** Merchant-facing names, in the surfaces' own wire order. */
function labelSurfaces(surfaces: readonly AgenticLogicSurface[]): string {
  return surfaces
    .map((surface) => AGENTIC_LOGIC_SURFACE_LABEL[surface])
    .join(", ");
}

/**
 * The ONE rendering of "where this rule's evaluation budget came from",
 * composed here so the gate `result` line, the gate detail renderer in the
 * workspace and any future consumer say the same thing.
 *
 * Written because the budget-bearing gates used to publish the threshold as a
 * bare number, and the workspace — the only surface that has to explain it —
 * called it "the assigned budget" for rules assigned nowhere. The word
 * "assigned" appears in the output IF AND ONLY IF `basis` is `"assigned"`.
 *
 * `basis: undefined` is the legacy job row: a manifest frozen before the field
 * existed genuinely cannot say which world it was in, so the sentence makes NO
 * claim rather than inventing the likeliest one.
 *
 * The surface the sentence names is DERIVED from `surfaces` here rather than
 * passed in, so it cannot disagree with
 * {@link agenticLogicTightestSurfaceBudgetMs}.
 */
export function renderAgenticLogicBudgetBasis(input: {
  budgetMs: number;
  surfaces: readonly AgenticLogicSurface[];
  basis: AgenticLogicBudgetBasis | undefined;
}): string {
  const { budgetMs, surfaces, basis } = input;
  if (basis === undefined) {
    return `Held to a ${budgetMs}ms evaluation budget.`;
  }
  const cap = `${budgetMs}ms ${AGENTIC_LOGIC_SURFACE_LABEL[tightestSurface(surfaces)]} cap`;
  if (basis === "any_surface") {
    return (
      `Held to the ${cap} — the tightest cap on any surface, because this ` +
      `rule declares no surfaces yet.`
    );
  }
  if (basis === "declared") {
    return (
      `Held to the ${cap}, the tightest of the surfaces this rule declares ` +
      `(${labelSurfaces(surfaces)}). The rule is not assigned to a placement ` +
      `yet, so it is gated at the tightest cap it could later be placed on.`
    );
  }
  return surfaces.length > 1
    ? `Held to the ${cap}, the tightest of the surfaces this rule is assigned to (${labelSurfaces(surfaces)}).`
    : `Held to the ${cap} — the surface this rule is assigned to.`;
}

/**
 * Broker overhead subtracted from the tightest assigned surface budget when
 * the chain-cost gate prices a rule's measured exhaustive wall time:
 * measured ≤ tightestAssignedBudgetMs − AGENTIC_LOGIC_BROKER_OVERHEAD_MS.
 *
 * OVERHEAD MEANS the full broker envelope MINUS rule time — pre-execution AND
 * post-execution. `measured` above is rule-only wall time, so anything left
 * out of the envelope is budget this gate hands out that the absolute serving
 * deadline still charges.
 *
 * DERIVED, not guessed: `ceil((measured warm CPU p95 + I/O allowance) / 25) *
 * 25` = `ceil((0.93 + 165) / 25) * 25` = 175. The CPU p95 comes from
 * `web/node-backend/src/scripts/agentic-logic/benchmark-serving-broker-overhead.ts`;
 * the 165 ms I/O allowance is itemized one row per awaited dependency — each
 * with its assumed p95 and the basis for it — in
 * `docs/agentic-logic/fleet-capacity.md`. Change this number only together
 * with that evidence.
 *
 * The broker deliberately does NOT import this constant into
 * `effectiveDeadlineAtMs`: its deadline is already absolute from
 * `evaluationStartedAtMs`, so every pre-execution step is charged for real,
 * and subtracting a static estimate on top would double-charge and shrink the
 * rule's live budget below the surface cap.
 *
 * Consumers must import it rather than inline a number so a recalibration is
 * one edit.
 */
export const AGENTIC_LOGIC_BROKER_OVERHEAD_MS = 175;

/**
 * How many times `runtime_under_budget` re-runs a scenario that MISSED its
 * budget, and averages instead of the first run.
 *
 * Only on a miss. A run that came in under budget proved the rule fits, and
 * re-running it would spend live reads to re-confirm what already passed — so
 * the fast path stays one invocation. A run that missed is the ambiguous case:
 * a validation invocation crosses two Lambdas that may cold-start and a read
 * cache that is empty on first touch, so the first measurement can be the
 * harness's cost rather than the rule's. The retries answer that, and the mean
 * keeps one unlucky retry from deciding the verdict either way.
 *
 * Every retry is another live read of the org's data against the same Admin
 * budget serving spends, which is why they are not paid up front. Three is the
 * smallest count where a single slow retry cannot swing the average alone.
 */
export const AGENTIC_LOGIC_RUNTIME_RETRY_COUNT = 3;
